import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { SlidersHorizontal, Map as MapIcon, List } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SearchBar } from "@/components/SearchBar";
import { FiltersPanel } from "@/components/FiltersPanel";
import { CommunityCard } from "@/components/CommunityCard";
import { MapView } from "@/components/MapView";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { get } from "@/lib/api";

const FILTER_KEYS = ["q", "state", "city", "min_fee", "max_fee", "pets", "rental", "amenities", "type", "status", "gated"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [showMap, setShowMap] = useState(() => window.innerWidth >= 1024);
  const filters = Object.fromEntries(FILTER_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
  const sort = params.get("sort") || "featured";
  const page = Number(params.get("page") || 1);

  const { data, isLoading } = useQuery({
    queryKey: ["search", filters, sort, page],
    queryFn: () => get("/communities", { ...filters, sort, page, limit: 24 }),
    placeholderData: keepPreviousData,
  });
  const { data: points = [] } = useQuery({ queryKey: ["map", filters], queryFn: () => get("/communities/map", filters), enabled: showMap });

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === "" || v == null ? next.delete(k) : next.set(k, v)));
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };
  const reset = () => setParams(params.get("q") ? { q: params.get("q") } : {});

  return (
    <div data-testid="search-page">
      <Seo title={filters.q ? `HOAs matching “${filters.q}”` : "Search HOA Communities"} description="Filter HOA communities nationwide by fees, pet rules, rental restrictions and amenities." path="/search" />
      <h1 className="sr-only">{filters.q ? `HOAs matching ${filters.q}` : "Search HOA communities"}</h1>
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex-1 max-w-2xl"><SearchBar size="sm" initial={filters.q || ""} testid="search-page" key={filters.q || ""} /></div>
          <div className="flex flex-wrap items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <button className="lg:hidden inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold text-[#0B2150]" data-testid="mobile-filters-btn"><SlidersHorizontal className="h-4 w-4" />Filters</button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-white overflow-y-auto">
                <SheetTitle className="mb-4">Filters</SheetTitle>
                <FiltersPanel params={params} set={set} reset={reset} />
              </SheetContent>
            </Sheet>
            <label htmlFor="sort" className="sr-only">Sort results</label>
            <select id="sort" value={sort} onChange={(e) => set({ sort: e.target.value })} data-testid="search-sort-select"
              className="rounded-full border border-[#CBD5E1] bg-white px-4 py-2 text-sm">
              <option value="featured">Featured</option><option value="fee_asc">Fee: low to high</option>
              <option value="fee_desc">Fee: high to low</option><option value="rating">Top rated</option><option value="name">Name A-Z</option>
            </select>
            <button onClick={() => setShowMap((m) => !m)} data-testid="map-toggle-button" aria-pressed={showMap}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#0B2150] text-white px-4 py-2 text-sm font-semibold hover:bg-[#03A896] transition-colors">
              {showMap ? <><List className="h-4 w-4" />List<span className="hidden sm:inline"> only</span></> : <><MapIcon className="h-4 w-4" /><span className="hidden sm:inline">Show </span>Map</>}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8 flex gap-8">
        <aside className="hidden lg:block w-64 shrink-0" aria-label="Search filters">
          <div className="sticky top-28"><FiltersPanel params={params} set={set} reset={reset} /></div>
        </aside>
        <div className={`flex-1 min-w-0 ${showMap ? "xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] xl:gap-6" : ""}`}>
          {showMap && (
            <div className="xl:order-2 mb-6 xl:mb-0 h-[420px] xl:h-[calc(100vh-9rem)] xl:sticky xl:top-28">
              <MapView points={points} />
            </div>
          )}
          <div>
            <p className="text-sm text-slate-600 mb-4" aria-live="polite" data-testid="search-results-count">
              {isLoading ? "Searching…" : `${data?.total?.toLocaleString() ?? 0} communities found`}
            </p>
            <div className={`grid gap-5 sm:grid-cols-2 ${showMap ? "2xl:grid-cols-2" : "xl:grid-cols-3"}`} data-testid="search-results-grid">
              {isLoading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}
              {data?.items?.map((c) => <CommunityCard key={c.id} c={c} />)}
            </div>
            {data && data.total === 0 && (
              <div className="rounded-2xl border border-dashed p-10 text-center text-slate-600" data-testid="search-empty-state">
                No communities match these filters. Try widening the fee range or removing a filter.
              </div>
            )}
            {data && data.pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-10" data-testid="search-pagination">
                <button disabled={page <= 1} onClick={() => set({ page: page - 1 })} className="rounded-full border px-4 py-2 text-sm disabled:opacity-40" data-testid="pagination-prev">Previous</button>
                <span className="text-sm text-slate-600">Page {page} of {data.pages}</span>
                <button disabled={page >= data.pages} onClick={() => set({ page: page + 1 })} className="rounded-full border px-4 py-2 text-sm disabled:opacity-40" data-testid="pagination-next">Next</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
